import { closestCenter, DndContext } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
// components/ReportBuilder.js
import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch, reportStorage } from '../api';
import { ADMIN_BASE, API_BASE } from '../apiBase';
import {
  calculateMaxClosingDays,
  calculateMaxDealDuration,
  calculateMaxFeasibilityDays,
  calculateMaxZoningDays,
} from '../reportTerms';
import ColumnSettingsModal from './ColumnSettingsModal';
import CompPreview from './CompPreview';
import { generatePDFReport } from './PDFgenerator';

function formatDollar(value, decimals = 0) {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  return isNaN(num)
    ? ''
    : `$${num.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}`;
}

function formatInteger(value) {
  if (value === null || value === undefined) return '';
  const num = Number(value);
  return isNaN(num) ? '' : Math.round(num).toString();
}

function formatDate(value, formatOption = 'Date') {
  if (!value) return '';

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    console.warn('Invalid date:', value);
    return '';
  }

  switch (formatOption) {
    case 'Month/Year':
      return date.toLocaleString('default', {
        month: 'short',
        year: 'numeric',
      });
    case 'Quarter/Year': {
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `Q${quarter} ${date.getFullYear()}`;
    }
    case 'Year':
      return `${date.getFullYear()}`;
    default:
      return date.toLocaleDateString();
  }
}

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportBuilder = ({
  reportData,
  cartItems,
  setCartItems,
  onBack,
  onRemoveItem,
  onPreviewPDF,
  userData,
  savedReport,
  onNavigateToMap,
  isEditingReport,
  onReturnToMap,
  initialGeoData,
  reportId,
  reportStatus,
  onContextUpdate,
  liveRows,
  setLiveRows,
  reportBuilderState,
  initialOfferSortModel = [],
  initialFeedbackSortModel = [],
  initialStatusFilter = [],
  initialDateFilter = null,
  initialActiveTab = 0,
  ...props
}) => {
  console.log('Cart Items from MapPage:', cartItems);
  const [dataGridState, setDataGridState] = useState({
    sortModel: [],
    offerSortModel: [],
    feedbackSortModel: [],
    filterModel: { items: [] },
    paginationModel: { page: 0, pageSize: 10 },
  });

  const [dateFilter, setDateFilter] = useState(initialDateFilter);

  const STATUS_ORDER = {
    'Offer made': 1,
    'Making offer': 2,
    Interested: 3,
    Reviewing: 4,
    'Received email': 5,
    Pass: 6,
  };

  const DEFAULT_FEEDBACK_SORT_MODEL = [
    {
      field: 'status',
      sort: 'asc',
      sortComparator: (v1, v2) =>
        (STATUS_ORDER[v1] || 99) - (STATUS_ORDER[v2] || 99),
    },
    {
      field: 'feedback',
      sort: 'desc', // Changed to 'desc'
      sortComparator: (v1, v2) => (v2?.length || 0) - (v1?.length || 0), // Now sorts longer comments first
    },
  ];
  const [editingField, setEditingField] = useState('');
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [editingPricingRowId, setEditingPricingRowId] = useState(null);
  const [editingPricingValue, setEditingPricingValue] = useState('');
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [offerSortModel, setOfferSortModel] = useState(initialOfferSortModel);
  const [feedbackSortModel, setFeedbackSortModel] = useState(() => {
    // Use initialFeedbackSortModel if provided, otherwise use our default
    return initialFeedbackSortModel?.length > 0
      ? initialFeedbackSortModel
      : DEFAULT_FEEDBACK_SORT_MODEL;
  });
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter);
  const [isRowModalOpen, setIsRowModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [refreshKey, setRefreshKey] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [generatedComment, setGeneratedComment] = useState('');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [reportGeoData, setReportGeoData] = useState(() => {
    if (savedReport?.geo_data) {
      try {
        return typeof savedReport.geo_data === 'string'
          ? JSON.parse(savedReport.geo_data)
          : savedReport.geo_data;
      } catch (e) {
        console.error('🛑 Failed to parse saved report geo_data:', e);
        return null;
      }
    }
    return initialGeoData || null;
  });

  const [offerPagination, setOfferPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  const [feedbackPagination, setFeedbackPagination] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    console.log('ReportBuilder received geoData:', {
      type: 'FeatureCollection',
      features: Object.entries(initialGeoData || {}).map(([id, data]) => ({
        type: 'Feature',
        id,
        geometry: data.geometry,
        properties: data.properties,
      })),
    });
  }, [initialGeoData]);

  useEffect(() => {
    if (initialGeoData) {
      setReportGeoData(initialGeoData);
    }
  }, [initialGeoData]);

  useEffect(() => {
    if (activeTab === 1 && feedbackSortModel.length === 0) {
      setFeedbackSortModel(DEFAULT_FEEDBACK_SORT_MODEL);
    }
  }, [activeTab]);

  const [isLoading, setIsLoading] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [editingCommentRowId, setEditingCommentRowId] = useState(null);
  const [editingCommentValue, setEditingCommentValue] = useState('');

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [editingStatusRowId, setEditingStatusRowId] = useState(null);
  const [editingStatusValue, setEditingStatusValue] = useState('');

  const [offers, setOffers] = useState(reportData?.offers || []);
  const [offersColumns, setOffersColumns] = useState([
    { field: 'name', headerName: 'Offer Name', width: 200 },
    { field: 'offer_status', headerName: 'Status', width: 150 },
    {
      field: 'calculated_purchase_price',
      headerName: 'Purchase Price',
      width: 150,
    },
    { field: 'effective_date', headerName: 'Effective Date', width: 150 },
    { field: 'offering_company_name', headerName: 'Company', width: 200 },
  ]);

  // Helper function to format extension summaries
  const formatExtensionSummary = (count, days, cost, type) => {
    if (!Number(count)) {
      return `No ${type.toLowerCase()} extensions`;
    }

    const formattedCost = cost ? formatDollar(cost) : '$0';
    return `(${Number(count)}) ${Number(days) || 0}-day ${type.toLowerCase()} extensions costing ${formattedCost} per extension`;
  };

  const [offerRowOrder, setOfferRowOrder] = useState(() => {
    // Use saved order if available
    if (reportData?.offerRowOrder) return reportData.offerRowOrder;
    // Otherwise create from current offers
    return (reportData?.offers || []).map((o) => o.id);
  });
  const [feedbackRowOrder, setFeedbackRowOrder] = useState(() => {
    // Use saved order if available
    if (reportData?.feedbackRowOrder) return reportData.feedbackRowOrder;
    // Otherwise create from current feedback
    return (reportData?.feedback || []).map((f) => f.id);
  });

  const [excludeOffers, setExcludeOffers] = useState(
    savedReport?.excludeOffers || reportData?.excludeOffers || false,
  );
  const [excludeFeedback, setExcludeFeedback] = useState(
    savedReport?.excludeFeedback || reportData?.excludeFeedback || false,
  );
  const baseColumnProps = {
    minWidth: 120, // Default minimum width
    filterable: true, // Default to filterable
    disableColumnMenu: false, // Default menu behavior
    hidden: false, // Default visibility
  };

  const offerColumns = [
    {
      ...baseColumnProps,
      field: 'offering_contact_name',
      headerName: 'Contact',
      width: 200,
      sortable: true,
    },
    {
      ...baseColumnProps,
      field: 'offering_company_name',
      headerName: 'Company',
      width: 200,
      sortable: true,
    },
    {
      ...baseColumnProps,
      field: 'calculated_purchase_price',
      headerName: 'Purchase Price',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.calculated_purchase_price;
        return formatDollar(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'earnest_money',
      headerName: 'Initial Earnest Money',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.earnest_money;
        return formatDollar(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'additional_earnest_money',
      headerName: 'Additional Earnest Money',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.additional_earnest_money;
        return formatDollar(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'Total_earnest_money',
      headerName: 'Total Earnest Money',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.Total_earnest_money;
        return formatDollar(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'per_unit_pricing',
      headerName: 'Per Unit Pricing',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.per_unit_pricing;
        const { id } = params;

        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setEditingPricingRowId(id);
              setEditingPricingValue(val?.toString() || '');
              setEditingField('per_unit_pricing');
              setPricingModalOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {val != null && val !== '' ? (
              formatDollar(val)
            ) : (
              <span style={{ color: '#888' }}>Click to add price</span>
            )}
          </Box>
        );
      },
    },
    {
      ...baseColumnProps,
      field: 'per_lot_pricing',
      headerName: 'Per Lot Pricing',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.per_lot_pricing;
        const { id } = params;

        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setEditingPricingRowId(id);
              setEditingPricingValue(val?.toString() || '');
              setEditingField('per_lot_pricing');
              setPricingModalOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {val != null && val !== '' ? (
              formatDollar(val)
            ) : (
              <span style={{ color: '#888' }}>Click to add price</span>
            )}
          </Box>
        );
      },
    },
    {
      ...baseColumnProps,
      field: 'primary_use_type',
      headerName: 'Primary Uses',
      width: 200,
      sortable: true,
    },
    {
      ...baseColumnProps,
      field: 'feasibility_period',
      headerName: 'Feasibility Period (Days)',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.feasibility_period;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'number_of_feasibility_extensions',
      headerName: 'Feasibility Extensions',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.number_of_feasibility_extensions;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'days_in_each_feasibility_extension',
      headerName: 'Days in Feasibility Extensions',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.days_in_each_feasibility_extension;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'cost_of_feasibility_extension',
      headerName: 'Cost of Feasibility Extension',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.cost_of_feasibility_extension;
        return formatDollar(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'zoning_approval_period',
      headerName: 'Zoning Approval (Days)',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.zoning_approval_period;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'number_of_zoning_extensions',
      headerName: 'Zoning Extensions',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.number_of_zoning_extensions;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'days_in_each_zoning_extension',
      headerName: 'Days in Zoning Extensions',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.days_in_each_zoning_extension;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'cost_of_zoning_extension',
      headerName: 'Cost of Zoning Extension',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.cost_of_zoning_extension;
        return formatDollar(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'closing_period',
      headerName: 'Closing Period (Days)',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.closing_period;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'number_of_closing_extensions',
      headerName: 'Closing Extensions',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.number_of_closing_extensions;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'days_in_each_closing_extension',
      headerName: 'Days in Closing Extensions',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.days_in_each_closing_extension;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'cost_of_closing_extension',
      headerName: 'Cost of Closing Extension',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.cost_of_closing_extension;
        return formatDollar(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'Max_Feasibility_Days',
      headerName: 'Max Feasibility Days',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.Max_Feasibility_Days;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'Max_Zoning_Days',
      headerName: 'Max Zoning Days',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.Max_Zoning_Days;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'Max_Closing_Days',
      headerName: 'Max Closing Days',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.Max_Closing_Days;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'Max_Deal_Duration',
      headerName: 'Max Deal Duration (Days)',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.Max_Deal_Duration;
        return formatInteger(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'Feasibility_Extensions_Summary',
      headerName: 'Feasibility Extensions',
      width: 300,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value || '—'}</span>
        </Tooltip>
      ),
    },
    {
      ...baseColumnProps,
      field: 'Zoning_Extensions_Summary',
      headerName: 'Zoning Extensions',
      width: 300,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value || '—'}</span>
        </Tooltip>
      ),
    },
    {
      ...baseColumnProps,
      field: 'Closing_Extensions_Summary',
      headerName: 'Closing Extensions',
      width: 300,
      minWidth: 250,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ''}>
          <span>{params.value || '—'}</span>
        </Tooltip>
      ),
    },
    {
      ...baseColumnProps,
      field: 'offer_report_comments',
      headerName: 'Offer Comments',
      width: 300,
      sortable: true,
      renderCell: (params) => {
        const { id, value } = params;

        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setEditingCommentRowId(id);
              setEditingCommentValue(value || '');
              setEditingField('offer_report_comments');
              setCommentModalOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {value?.trim() ? (
              value
            ) : (
              <span style={{ color: '#888' }}>Click to add comment</span>
            )}
          </Box>
        );
      },
    },
    {
      ...baseColumnProps,
      field: 'calculated_psf',
      headerName: 'Calc. PSF price',
      width: 160,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => {
        const val = params.row?.calculated_psf;
        return formatDollar(val, 2);
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 160, // Increased width to accommodate both icons
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      isSpecial: true,
      hideInSettings: true,
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() =>
              window.open(`${ADMIN_BASE}/offer/${params?.id || ''}`, '_blank')
            }
            sx={{ padding: '8px' }} // Optional: Adjust padding for better spacing
          >
            <OpenInNewIcon fontSize='small' />
          </IconButton>

          <IconButton
            onClick={() => handleRemoveRow(params.id)}
            sx={{ padding: '8px' }} // Optional: Adjust padding for better spacing
          >
            <CloseIcon fontSize='small' />{' '}
            {/* Make sure to import CloseIcon from @mui/icons-material */}
          </IconButton>
        </>
      ),
    },
  ];

  const STATUS_OPTIONS = [
    'Offer made',
    'Making offer',
    'Interested',
    'Reviewing',
    'Received email',
    'Pass',
  ];

  const feedbackColumns = [
    {
      ...baseColumnProps,
      field: 'name',
      headerName: 'Feedback Name',
      width: 200,
      minWidth: 180,
      sortable: false,
    },
    {
      ...baseColumnProps,
      field: 'status',
      headerName: 'Status',
      width: 150,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const { id, value } = params;
        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setEditingStatusRowId(id);
              setEditingStatusValue(value || '');
              setEditingField('status');
              setStatusModalOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {value?.trim() || '—'}
          </Box>
        );
      },
    },
    {
      ...baseColumnProps,
      field: 'feedback',
      headerName: 'Feedback',
      width: 300,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => {
        const { id, value } = params;

        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setEditingCommentRowId(id);
              setEditingCommentValue(value || '');
              setEditingField('feedback');
              setCommentModalOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {value?.trim() ? (
              value
            ) : (
              <span style={{ color: '#888' }}>Click to add comment</span>
            )}
          </Box>
        );
      },
    },
    {
      ...baseColumnProps,
      field: 'buyer_company_name',
      headerName: 'Company',
      width: 200,
      minWidth: 180,
      sortable: false,
    },
    {
      ...baseColumnProps,
      field: 'buyer_contact_name',
      headerName: 'Contact',
      width: 200,
      minWidth: 180,
      sortable: false,
    },
    {
      ...baseColumnProps,
      field: 'created_date',
      headerName: 'Created Date',
      width: 150,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => {
        const val = params.row?.created_date;
        return formatDate(val);
      },
    },
    {
      ...baseColumnProps,
      field: 'feedback_report_comments',
      headerName: 'Feedback Comments',
      width: 300,
      sortable: false,
      renderCell: (params) => {
        const { id, value } = params;

        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              setEditingCommentRowId(id);
              setEditingCommentValue(value || '');
              setEditingField('feedback_report_comments');
              setCommentModalOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {value?.trim() ? (
              value
            ) : (
              <span style={{ color: '#888' }}>Click to add comment</span>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 160, // Increased width to accommodate both icons
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      isSpecial: true,
      hideInSettings: true,
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() =>
              window.open(
                `${ADMIN_BASE}/buyer_feedback/${params?.id || ''}`,
                '_blank',
              )
            }
            sx={{ padding: '8px' }} // Optional: Adjust padding for better spacing
          >
            <OpenInNewIcon fontSize='small' />
          </IconButton>

          <IconButton
            onClick={() => handleRemoveRow(params.id)}
            sx={{ padding: '8px' }} // Optional: Adjust padding for better spacing
          >
            <CloseIcon fontSize='small' />{' '}
            {/* Make sure to import CloseIcon from @mui/icons-material */}
          </IconButton>
        </>
      ),
    },
  ];

  const [offerColumnSettings, setOfferColumnSettings] = useState(() => {
    if (reportData?.columnSettings?.offers)
      return reportData.columnSettings.offers;
    try {
      const saved = reportStorage.getItem('listingReportOfferColumns');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [feedbackColumnSettings, setFeedbackColumnSettings] = useState(() => {
    if (reportData?.columnSettings?.feedback)
      return reportData.columnSettings.feedback;
    try {
      const saved = reportStorage.getItem('listingReportFeedbackColumns');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const [feedback, setFeedback] = useState(reportData?.feedback || []);

  useEffect(() => {
    if (offers.length > 0 && offerRowOrder.length === 0) {
      setOfferRowOrder(offers.map((o) => o.id));
    }
  }, [offers, offerRowOrder.length]);

  useEffect(() => {
    if (feedback.length > 0 && feedbackRowOrder.length === 0) {
      setFeedbackRowOrder(feedback.map((f) => f.id));
    }
  }, [feedback, feedbackRowOrder.length]);

  const [isOffersModalOpen, setIsOffersModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    if (reportData) {
      setOffers(reportData.offers || []);
      setFeedback(reportData.feedback || []);
      setExcludeOffers(reportData.excludeOffers || false);
      setExcludeFeedback(reportData.excludeFeedback || false);
    }
    setIsLoading(false);
  }, [reportData]);

  // Add this useEffect to handle data updates
  useEffect(() => {
    if (reportData) {
      setOffers(reportData.offers || []);
      setFeedback(reportData.feedback || []);
      setOfferRowOrder(reportData.offerRowOrder || []);
      setFeedbackRowOrder(reportData.feedbackRowOrder || []);
    }
  }, [reportData]);

  // In ReportBuilder's handleInternalPreview:
  console.log('Sending to preview:', {
    offers: offers.length,
    feedback: feedback.length,
    offerRowOrder: offerRowOrder.length,
    feedbackRowOrder: feedbackRowOrder.length,
  });

  // In ReportBuilder.js
  const handleInternalPreview = async () => {
    setIsGeneratingPDF(true);

    try {
      // ===== 1. PREPARE REPORT SECTIONS (EXISTING CODE) =====
      const sections = [];

      // Filter feedback rows based on status filter if any statuses are selected
      const filteredFeedbackRows =
        statusFilter.length > 0
          ? stableFeedbackRows.filter((row) =>
              statusFilter.includes(row.status),
            )
          : stableFeedbackRows;

      if (!excludeOffers && sortedOfferRows.length > 0) {
        sections.push({
          title: 'Offers',
          rows: sortedOfferRows.map((row, index) => ({
            ...row,
            rowNumber: index + 1,
            effective_date:
              offerColumnSettings['effective_date']?.maskUnderContract &&
              row.offer_status === 'Under Contract'
                ? '-'
                : row.effective_date,
            calculated_purchase_price: formatDollar(
              row.calculated_purchase_price,
            ),
            Total_earnest_money: formatDollar(row.Total_earnest_money),
            earnest_money: formatDollar(row.earnest_money),
            additional_earnest_money: formatDollar(
              row.additional_earnest_money,
            ),
            per_unit_pricing: formatDollar(row.per_unit_pricing),
            per_lot_pricing: formatDollar(row.per_lot_pricing),
            cost_of_feasibility_extension: formatDollar(
              row.cost_of_feasibility_extension,
            ),
            cost_of_zoning_extension: formatDollar(
              row.cost_of_zoning_extension,
            ),
            cost_of_closing_extension: formatDollar(
              row.cost_of_closing_extension,
            ),
            calculated_psf: formatDollar(row.calculated_psf, 2),
          })),
          columns: offersColumnsToShow.filter((col) => col.field !== 'actions'),
        });
      }

      if (!excludeFeedback && sortedFeedbackRows.length > 0) {
        sections.push({
          title: 'Feedback',
          rows: sortedFeedbackRows.map((row, index) => ({
            ...row,
            rowNumber: index + 1,
          })),
          columns: feedbackColumnsToShow.filter(
            (col) => col.field !== 'actions',
          ),
        });
      }

      // Excluding both tabs, or filtering every row away, leaves nothing to
      // draw. jsPDF still emits a valid empty page, so the size check below
      // cannot catch it.
      if (sections.length === 0) {
        throw new Error(
          'the report has no offers and no feedback to show. Clear a filter or an exclude checkbox',
        );
      }

      // ===== 2. FETCH COVER PAGE (NEW CODE) =====
      let coverPagePdf = null;
      try {
        const response = await apiFetch(
          `${API_BASE}/listings/${cartItems[0].properties.listingId}/OM`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );

        if (response.ok) {
          coverPagePdf = await response.arrayBuffer();
        } else {
          const err = await response.text();
          console.error('❌ Error fetching cover page:', err);
        }
      } catch (error) {
        console.error("Couldn't fetch cover page:", error);
        // Continue without cover page if there's an error
      }

      // ===== 3. GENERATE MAIN REPORT (EXISTING CODE) =====
      const { tabularPDF, mapPDF } = await generatePDFReport(
        sections,
        reportTitle,
        { orientation: 'landscape' },
        activeTab === 0 ? reportGeoData : {},
      );

      // ===== 4. COMBINE PDFs (NEW CODE) =====
      const { PDFDocument } = await import('pdf-lib');
      const finalPdf = await PDFDocument.create();

      // Add cover page if available
      if (coverPagePdf) {
        const coverDoc = await PDFDocument.load(coverPagePdf);
        const [coverPage] = await finalPdf.copyPages(coverDoc, [0]);
        finalPdf.addPage(coverPage);
      }

      // Add main report pages
      const mainDoc = await PDFDocument.load(tabularPDF);
      const pageIndices = Array.from(
        { length: mainDoc.getPageCount() },
        (_, i) => i,
      );
      const reportPages = await finalPdf.copyPages(mainDoc, pageIndices);
      reportPages.forEach((page) => finalPdf.addPage(page));

      // ===== 5. FINALIZE PDF (MODIFIED CODE) =====
      const mergedPdfBytes = await finalPdf.save();
      const pdfBlob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      if (pdfBlob.size === 0) {
        throw new Error('The generated report PDF is empty.');
      }
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // ===== 6. PREPARE PAYLOAD (EXISTING CODE) =====
      const previewPayload = {
        reportTitle,
        status: reportStatus,
        report_id: reportId || null,
        geoData: reportGeoData,
        mapPDF,
        currentRows: activeTab === 0 ? sortedOfferRows : sortedFeedbackRows,
        reportOfferCount: [...sortedOfferRows],
        reportFeedbackCount: [...sortedFeedbackRows],
        currentCartItems: cartItems,
        currentRowOrder: activeTab === 0 ? offerRowOrder : feedbackRowOrder,
        columnSettings: {
          offers: offerColumnSettings,
          feedback: feedbackColumnSettings,
        },
        excludeOffers,
        excludeFeedback,
        offers: [...offers],
        feedback: [...feedback],
        offerRowOrder: [...offerRowOrder],
        feedbackRowOrder: [...feedbackRowOrder],
        dataGridState: {
          sortModel,
          offerSortModel,
          feedbackSortModel,
          filterModel: { items: [] },
          paginationModel: { page: 0, pageSize: 10 },
        },
        statusFilter: [...statusFilter],
        dateFilter: dateFilter,
        activeTab,
      };

      if (onPreviewPDF) {
        onPreviewPDF(pdfUrl, previewPayload, pdfBlob);
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      setPdfError(
        `Could not build the report PDF: ${error?.message ?? error}. Nothing was published.`,
      );
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const RowOrderModal = React.memo(
    ({
      open,
      onClose,
      rows,
      rowOrder,
      setRowOrder,
      rowType,
      sortModel,
      onSortModelChange,
    }) => {
      // Create a map of rows by ID for quick lookup
      const rowsById = useMemo(() => {
        const map = {};
        rows.forEach((row) => {
          const id = row.id; // Handle both Id and id cases
          if (id) map[id] = row;
        });
        return map;
      }, [rows]);

      const displayOrder = useMemo(() => {
        // If there's a sort model, use the sorted rows order
        if (sortModel && sortModel.length > 0) {
          return rows.map((row) => row.id);
        }
        // Otherwise use the manually reordered rows
        return rowOrder;
      }, [rows, rowOrder, sortModel]);

      // Filter displayOrder to only include IDs that exist in our data
      const filteredDisplayOrder = useMemo(() => {
        return displayOrder.filter((id) => rowsById[id]);
      }, [displayOrder, rowsById]);

      // Filter rowOrder to only include IDs that exist in our data
      const filteredRowOrder = useMemo(() => {
        return rowOrder.filter((id) => rowsById[id]);
      }, [rowOrder, rowsById]);

      // Handle drag end event
      const handleModalDragEnd = (event) => {
        const { active, over } = event;
        if (active?.id !== over?.id) {
          setRowOrder((items) => {
            const oldIndex = items.indexOf(active.id);
            const newIndex = items.indexOf(over.id);
            return arrayMove(items, oldIndex, newIndex);
          });

          // Reset the sort model to ensure manual order takes precedence
          if (onSortModelChange) {
            onSortModelChange([]);
          }
        }
      };

      // Debug logs
      console.log('RowOrderModal data:', {
        rows: rows.slice(0, 3),
        rowOrder,
        filteredRowOrder,
        rowsById: Object.keys(rowsById).slice(0, 3),
      });

      if (!rows || rows.length === 0) {
        return (
          <Dialog open={open} onClose={onClose}>
            <DialogTitle>No {rowType} to reorder</DialogTitle>
            <DialogContent>
              <Typography>
                There are no {rowType} available to reorder.
              </Typography>
            </DialogContent>
          </Dialog>
        );
      }

      const SortableRow = ({ id, row, rowType }) => {
        const { attributes, listeners, setNodeRef, transform, transition } =
          useSortable({ id });

        const style = {
          transform: CSS.Transform.toString(transform),
          transition: transition || 'none',
          display: 'flex',
          alignItems: 'center',
          padding: '12px',
          marginBottom: '8px',
          backgroundColor: '#fff',
          borderRadius: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        };

        if (!row) {
          console.warn(`Missing row for ID: ${id}`);
          return null;
        }

        return (
          <div ref={setNodeRef} style={style} {...attributes}>
            <Box
              {...listeners}
              sx={{ cursor: 'grab', mr: 2, touchAction: 'none' }}
            >
              <DragIndicatorIcon />
            </Box>
            {rowType === 'offers' ? (
              <Typography variant='body1'>
                {row.offering_company_name ||
                  row.offering_contact_name ||
                  'Unnamed'}{' '}
                • {row.primary_use_type || '—'} •{' '}
                {formatDollar(row.calculated_purchase_price) || 0}
              </Typography>
            ) : (
              <Typography variant='body1'>
                {row.buyer_company_name || row.buyer_contact_name || 'Unnamed'}{' '}
                • {row.status || 'No Status'}
              </Typography>
            )}
          </div>
        );
      };

      const MemoizedSortableRow = React.memo(SortableRow);

      return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
          <DialogTitle>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
            >
              <Box display='flex' alignItems='center' gap={1}>
                <TableRowsOutlinedIcon color='primary' />
                <Typography variant='h6' fontWeight='bold'>
                  Reorder {rowType === 'offers' ? 'Offers' : 'Feedback'}
                </Typography>
              </Box>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleModalDragEnd}
            >
              <SortableContext
                items={filteredDisplayOrder}
                strategy={verticalListSortingStrategy}
              >
                {filteredDisplayOrder.map((id) => {
                  const row = rowsById[id];
                  return row ? (
                    <MemoizedSortableRow
                      key={id}
                      id={id}
                      row={row}
                      rowType={rowType}
                    />
                  ) : null;
                })}
              </SortableContext>
            </DndContext>
          </DialogContent>
        </Dialog>
      );
    },
  );

  const [reportTitle, setReportTitle] = useState(() => {
    if (savedReport?.title) return savedReport.title;
    try {
      const savedTitle = reportStorage.getItem('listingReportTitle');
      return savedTitle || 'Listing Report Builder';
    } catch (e) {
      console.error('Failed to load report title:', e);
      return 'Listing Report Builder';
    }
  });

  // Initialize rowOrder from localStorage or from cartItems
  const [rowOrder, setRowOrder] = useState(() => {
    // Use saved row order if available
    if (savedReport?.row_order) {
      // Check if it's already an array
      if (Array.isArray(savedReport.row_order)) {
        return savedReport.row_order;
      }
      // Otherwise try to parse it
      try {
        return JSON.parse(savedReport.row_order);
      } catch (e) {
        console.error('Failed to parse saved row order:', e);
      }
    }
    try {
      const savedOrder = reportStorage.getItem('compReportRowOrder');
      return savedOrder
        ? JSON.parse(savedOrder)
        : cartItems.map((item) => item.id || item.properties?.id);
    } catch (e) {
      return cartItems.map((item) => item.id || item.properties?.id);
    }
  });

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const rows = props.rows;
  const setRows = props.setRows;

  const handleRemoveRow = React.useCallback(
    (id) => {
      console.log('🧹 Attempting to remove row with ID:', id);

      if (activeTab === 0) {
        // Remove from offers
        console.log('Current offers before removal:', offers);
        setOffers((prev) => prev.filter((row) => row.id !== id));
        setOfferRowOrder((prev) => prev.filter((rowId) => rowId !== id));
      } else {
        // Remove from feedback
        console.log('Current feedback before removal:', feedback);
        setFeedback((prev) => prev.filter((row) => row.id !== id));
        setFeedbackRowOrder((prev) => prev.filter((rowId) => rowId !== id));
      }

      // Call parent handler if provided
      if (onRemoveItem) {
        onRemoveItem(id);
      }

      console.log('Removal completed for ID:', id);
    },
    [activeTab, offers, feedback, onRemoveItem],
  );

  // Initialize rowOrder when rows change
  useEffect(() => {
    if (rows.length > 0 && rowOrder.length === 0) {
      setRowOrder(rows.map((row) => row.id));
    }
  }, [rows]);

  // Helper function for format options
  const getDefaultFormatOption = (field) => {
    if (field === 'closeDate') return 'Date';
    if (['purchasePrice', 'calculatedPrice', 'compPrice'].includes(field))
      return 'Total';
    return undefined;
  };

  // Save the title whenever it changes
  useEffect(() => {
    try {
      reportStorage.setItem('listingReportTitle', reportTitle);
    } catch (e) {
      console.error('Failed to save report title:', e);
    }
  }, [reportTitle]);

  // Save the title whenever it changes
  useEffect(() => {
    reportStorage.setItem('listingReportTitle', reportTitle);
  }, [reportTitle]);

  // Create DataGrid columns based on current configuration
  const getColumnsToShow = (baseColumns = [], settings = {}) => {
    const orderedFields =
      settings.__columnOrder || baseColumns.map((col) => col.field);

    return orderedFields
      .map((field) => {
        const base = baseColumns.find((col) => col.field === field);
        const config = settings[field] || {};

        if (!base || config.hidden) return null;

        return {
          ...base,
          headerName: config.label || base.headerName || base.field,
          ...(config.formatOption
            ? {
                valueFormatter: (params) => {
                  const val = params?.value ?? params?.row?.[field];
                  if (field.includes('date'))
                    return formatDate(val, config.formatOption);
                  if (field.includes('price') || field.includes('amount'))
                    return formatDollar(val);
                  return val ?? '';
                },
              }
            : base.valueFormatter
              ? { valueFormatter: base.valueFormatter }
              : {}),
        };
      })
      .filter(Boolean);
  };

  const offersColumnsToShow = getColumnsToShow(
    offerColumns.filter((col) => col.field !== 'actions'),
    offerColumnSettings,
  ).concat(offerColumns.find((col) => col.field === 'actions') || []);

  const feedbackColumnsToShow = getColumnsToShow(
    feedbackColumns.filter((col) => col.field !== 'actions'),
    feedbackColumnSettings,
  ).concat(feedbackColumns.find((col) => col.field === 'actions') || []);

  offersColumnsToShow.map((c) => ({
    field: c.field,
    isFormatter: typeof c.valueFormatter === 'function',
  }));

  console.log(
    'offersColumnsToShow check:',
    offersColumnsToShow.map((col) => ({
      field: col.field,
      hasFormatter: typeof col.valueFormatter === 'function',
    })),
  );

  const flattenedOffers = offers.map((offer) => {
    const companyName = offer?.offering_company_name;
    const fullName = offer?.offering_contact_name || '';
    const lastName = fullName.trim().split(' ').pop(); // Get last word (last name)

    return {
      ...offer,
      id: offer.id,
      lead_broker_name: offer?.lead_broker_name || '',
      offering_contact_name: offer?.offering_contact_name || '',
      offering_company_name:
        companyName || (lastName ? `${lastName} - INV` : 'N/A'),
      // New calculated fields
      Max_Feasibility_Days: calculateMaxFeasibilityDays(offer),
      Max_Zoning_Days: calculateMaxZoningDays(offer),
      Max_Closing_Days: calculateMaxClosingDays(offer),
      Max_Deal_Duration: calculateMaxDealDuration(offer),
      // New extension summary fields
      Feasibility_Extensions_Summary: formatExtensionSummary(
        offer.number_of_feasibility_extensions,
        offer.days_in_each_feasibility_extension,
        offer.cost_of_feasibility_extension,
        'Feasibility',
      ),
      Zoning_Extensions_Summary: formatExtensionSummary(
        offer.number_of_zoning_extensions,
        offer.days_in_each_zoning_extension,
        offer.cost_of_zoning_extension,
        'Zoning',
      ),
      Closing_Extensions_Summary: formatExtensionSummary(
        offer.number_of_closing_extensions,
        offer.days_in_each_closing_extension,
        offer.cost_of_closing_extension,
        'Closing',
      ),
    };
  });

  console.log('Flattened offer sample row:', flattenedOffers[0]);

  const stableOfferRows = useMemo(() => flattenedOffers, [flattenedOffers]);

  const flattenedFeedback = feedback.map((feedback) => {
    const companyName = feedback?.buyer_company_name;
    const fullName = feedback?.buyer_contact_name || '';
    const lastName = fullName.trim().split(' ').pop(); // Get last word (last name)

    return {
      ...feedback,
      id: feedback.id,
      buyer_company_name:
        companyName || (lastName ? `${lastName} - INV` : 'N/A'),
      buyer_contact_name: fullName || 'N/A',
    };
  });
  console.log('Flattened feedback sample row:', flattenedFeedback[0]);

  const applySorting = (rows, sortModel, manualOrder) => {
    if (!sortModel || sortModel.length === 0) {
      if (manualOrder && manualOrder.length > 0) {
        const rowMap = new Map(rows.map((row) => [row.id, row]));
        return manualOrder.map((id) => rowMap.get(id)).filter(Boolean);
      }
      return rows;
    }

    const sortedRows = [...rows];

    sortedRows.sort((a, b) => {
      for (const sortItem of sortModel) {
        const { field, sort, sortComparator } = sortItem;

        if (!field || !sort) continue;

        const aValue = a[field];
        const bValue = b[field];

        // ✅ Custom feedback sorting: longest to shortest
        if (field === 'feedback') {
          const lenA = aValue?.length || 0;
          const lenB = bValue?.length || 0;
          if (lenA !== lenB) return sort === 'asc' ? lenA - lenB : lenB - lenA;
          continue;
        }

        // ✅ Custom status sorting
        if (field === 'status') {
          const orderA = STATUS_ORDER[aValue] || 99;
          const orderB = STATUS_ORDER[bValue] || 99;
          if (orderA !== orderB)
            return sort === 'asc' ? orderA - orderB : orderB - orderA;
          continue;
        }

        // ✅ Generic sortComparator
        if (sortComparator) {
          const comparison = sortComparator(aValue, bValue);
          if (comparison !== 0)
            return sort === 'asc' ? comparison : -comparison;
          continue;
        }

        // ✅ Default comparisons
        if (aValue == null && bValue == null) continue;
        if (aValue == null) return sort === 'asc' ? -1 : 1;
        if (bValue == null) return sort === 'asc' ? 1 : -1;

        let comparison = 0;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }

        if (comparison !== 0) {
          return sort === 'asc' ? comparison : -comparison;
        }
      }

      return 0;
    });

    return sortedRows;
  };
  const stableFeedbackRows = useMemo(
    () => flattenedFeedback,
    [flattenedFeedback],
  );

  const sortedOfferRows = useMemo(() => {
    return applySorting(stableOfferRows, offerSortModel, offerRowOrder);
  }, [stableOfferRows, offerSortModel, offerRowOrder]);

  const sortedFeedbackRows = useMemo(() => {
    let filtered = stableFeedbackRows;

    // Apply status filter if any statuses are selected
    if (statusFilter.length > 0) {
      filtered = filtered.filter((row) => statusFilter.includes(row.status));
    }

    // Apply date filter if a date is selected
    if (dateFilter) {
      const filterDate = new Date(dateFilter);
      filtered = filtered.filter((row) => {
        const rowDate = new Date(row.created_date);
        return rowDate >= filterDate;
      });
    }

    return applySorting(filtered, feedbackSortModel, feedbackRowOrder);
  }, [
    stableFeedbackRows,
    feedbackSortModel,
    feedbackRowOrder,
    statusFilter,
    dateFilter,
  ]);

  useEffect(() => {
    return () => {
      // Reset the initial load flag when component unmounts
      setInitialLoadComplete(false);
    };
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1, sm: 2 },
          pt: 3,
          pb: 2,
          px: 2,
          borderBottom: '1px solid #eee',
          width: '100%',
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            order: 1,
          }}
        >
          {isEditingTitle ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                position: 'relative',
              }}
            >
              <TextField
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                onBlur={() => setIsEditingTitle(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setIsEditingTitle(false);
                  if (e.key === 'Escape') setIsEditingTitle(false);
                }}
                variant='standard'
                fullWidth
                autoFocus
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.3rem', sm: '1.5rem' },
                  '& .MuiInputBase-root': {
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    width: '100%',
                    pr: { xs: 4, sm: 6 },
                  },
                  '& .MuiInputBase-input': {
                    py: 0.5,
                    width: '100% !important',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  },
                }}
                InputProps={{
                  disableUnderline: true,
                }}
              />
              <IconButton
                onClick={() => setIsEditingTitle(false)}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  ml: 1,
                }}
              >
                <CheckIcon fontSize='small' />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <Typography
                variant='h5'
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.3rem', sm: '1.5rem' },
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  pr: 1,
                }}
              >
                {reportTitle}
              </Typography>
              <IconButton
                onClick={() => setIsEditingTitle(true)}
                sx={{
                  ml: 0.5,
                  p: 0.5,
                  flexShrink: 0,
                }}
              >
                <EditIcon fontSize='small' />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Sticky Icons */}
        {/* Sticky Icons */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #ddd',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: 'white',
            order: 2,
            alignSelf: { xs: 'flex-end', sm: 'center' },
            mt: { xs: 1, sm: 0 },
            minWidth: 'fit-content', // Add this to ensure container expands
            width: { xs: 'auto', sm: 'unset' }, // Adjust width behavior
          }}
        >
          {/* Cloud Icon - Opens the listing in Admin */}
          <Tooltip title='Open in Admin'>
            <IconButton
              onClick={() => {
                if (cartItems?.[0]?.properties?.listingId) {
                  window.open(
                    `${ADMIN_BASE}/seller_rep/${cartItems[0].properties.listingId}`,
                    '_blank',
                  );
                }
              }}
              disabled={!cartItems?.[0]?.properties?.listingId}
              sx={{
                p: { xs: 1, sm: 1.5 }, // Responsive padding
                borderRight: '1px solid #ddd',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <CloudQueueIcon
                sx={{
                  color: cartItems?.[0]?.properties?.listingId
                    ? 'gray'
                    : '#ccc',
                  fontSize: { xs: 'medium', sm: 'inherit' }, // Responsive icon size
                }}
              />
            </IconButton>
          </Tooltip>

          {/* PDF Icon - Opens shareable link */}
          <Tooltip title='Open PDF documents'>
            <IconButton
              onClick={() => {
                if (cartItems?.[0]?.properties?.shareableLink) {
                  window.open(cartItems[0].properties.shareableLink, '_blank');
                }
              }}
              disabled={!cartItems?.[0]?.properties?.shareableLink}
              sx={{
                p: { xs: 1, sm: 1.5 }, // Responsive padding
                borderRight: '1px solid #ddd',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <PictureAsPdfIcon
                sx={{
                  color: cartItems?.[0]?.properties?.shareableLink
                    ? 'gray'
                    : '#ccc',
                  fontSize: { xs: 'medium', sm: 'inherit' }, // Responsive icon size
                }}
              />
            </IconButton>
          </Tooltip>

          {/* Existing Row Order Icon */}
          <Tooltip
            title={
              activeTab === 1
                ? 'Row reordering not available on Feedback tab'
                : 'Reorder rows'
            }
          >
            <span>
              {' '}
              {/* Wrap IconButton in a span to support conditional disabling with Tooltip */}
              <IconButton
                onClick={() => {
                  if (activeTab !== 1) setIsRowModalOpen(true);
                }}
                disabled={activeTab === 1}
                sx={{
                  p: { xs: 1, sm: 1.5 },
                  borderRight: '1px solid #ddd',
                  '&:hover': {
                    backgroundColor:
                      activeTab === 1 ? 'transparent' : '#f5f5f5',
                  },
                  opacity: activeTab === 1 ? 0.5 : 1, // Optional: dim icon to show it's disabled
                  cursor: activeTab === 1 ? 'default' : 'pointer',
                }}
              >
                <TableRowsOutlinedIcon
                  sx={{
                    color: 'gray',
                    fontSize: { xs: 'medium', sm: 'inherit' },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          {/* Existing Column Settings Icon */}
          <Tooltip title='Column settings'>
            <IconButton
              onClick={() => setIsModalOpen(true)}
              sx={{
                p: { xs: 1, sm: 1.5 }, // Responsive padding
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <ViewColumnOutlinedIcon
                sx={{
                  color: 'gray',
                  fontSize: { xs: 'medium', sm: 'inherit' }, // Responsive icon size
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Replace the entire Scrollable Table Container section */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: '80px' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
        >
          <Tab label={`Offers (${offers.length})`} />
          <Tab label={`Feedback (${feedback.length})`} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeOffers}
                  onChange={(e) => setExcludeOffers(e.target.checked)}
                  disabled={flattenedOffers.length === 0}
                />
              }
              label='Exclude Offers from Report'
            />
          </Box>
          <DataGrid
            rows={sortedOfferRows}
            columns={offersColumnsToShow}
            getRowId={(row) => row.id}
            paginationModel={offerPagination}
            onPaginationModelChange={setOfferPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            pagination
            sortModel={offerSortModel}
            onSortModelChange={(model) => setOfferSortModel(model)}
            key={`offers-grid-${offers.length}`}
            disableColumnFilter
            disableColumnMenu
            sx={{
              '& .MuiDataGrid-footerContainer': {
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'background.paper',
              },
            }}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeFeedback}
                  onChange={(e) => setExcludeFeedback(e.target.checked)}
                  disabled={flattenedFeedback.length === 0}
                />
              }
              label='Exclude Feedback from Report'
            />

            {/* Custom Status Filter */}
            <Box sx={{ minWidth: 200 }}>
              <TextField
                select
                label='Filter by Status'
                SelectProps={{
                  multiple: true,
                  value: statusFilter,
                  onChange: (e) => setStatusFilter(e.target.value),
                  renderValue: (selected) => selected.join(', '),
                }}
                variant='outlined'
                size='small'
                fullWidth
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={statusFilter.includes(status)} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Date Filter */}
            <Box sx={{ minWidth: 200 }}>
              <TextField
                label='Filter by Date'
                type='date'
                value={dateFilter || ''}
                onChange={(e) => setDateFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                variant='outlined'
                size='small'
                fullWidth
              />
            </Box>

            {(statusFilter.length > 0 || dateFilter) && (
              <Button
                onClick={() => {
                  setStatusFilter([]);
                  setDateFilter(null);
                }}
                size='small'
                sx={{ ml: 1 }}
              >
                Clear All
              </Button>
            )}
          </Box>

          <DataGrid
            rows={sortedFeedbackRows}
            columns={feedbackColumnsToShow}
            getRowId={(row) => row.id}
            paginationModel={feedbackPagination}
            onPaginationModelChange={setFeedbackPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            pagination
            sortingMode='server'
            sortModel={DEFAULT_FEEDBACK_SORT_MODEL}
            onSortModelChange={() => {}}
            sortingOrder={['asc', 'desc']}
            key={`feedback-grid-${feedback.length}-${statusFilter.join(',')}`} // Force re-render on filter change
            disableColumnFilter
            disableColumnMenu
            sx={{
              '& .MuiDataGrid-footerContainer': {
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'background.paper',
              },
            }}
          />
        </TabPanel>
      </Box>

      {/* Sticky Footer Buttons */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          backgroundColor: 'white',
          borderTop: '1px solid #eee',
          p: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Button
          variant='outlined'
          onClick={() => {
            const fallbackBounds = {
              minLat: null,
              maxLat: null,
              minLng: null,
              maxLng: null,
            };

            // Compute bounds from current rows (if needed)
            const computeBoundsFromRows = () => {
              let minLat = Infinity,
                maxLat = -Infinity,
                minLng = Infinity,
                maxLng = -Infinity;

              rows.forEach((row) => {
                const lat = parseFloat(row.latitude);
                const lng = parseFloat(row.longitude);
                if (!isNaN(lat) && !isNaN(lng)) {
                  minLat = Math.min(minLat, lat);
                  maxLat = Math.max(maxLat, lat);
                  minLng = Math.min(minLng, lng);
                  maxLng = Math.max(maxLng, lng);
                }
              });

              if (minLat === Infinity || minLng === Infinity)
                return fallbackBounds;

              return {
                minLat,
                maxLat,
                minLng,
                maxLng,
              };
            };

            const bounds = savedReport
              ? {
                  minLat: savedReport.bounds_min_lat,
                  maxLat: savedReport.bounds_max_lat,
                  minLng: savedReport.bounds_min_lng,
                  maxLng: savedReport.bounds_max_lng,
                }
              : computeBoundsFromRows();

            const center = savedReport
              ? {
                  lat: savedReport.report_center_lat,
                  lng: savedReport.report_center_lng,
                }
              : {
                  lat: (bounds.minLat + bounds.maxLat) / 2,
                  lng: (bounds.minLng + bounds.maxLng) / 2,
                };

            const selectedIds = rows.map((r) => r.id || r.dealId);

            onReturnToMap({
              bounds,
              center,
              selectedIds,
              reportGeoData: reportGeoData,
            });
          }}
          startIcon={<MapOutlinedIcon />}
        >
          Return to Map
        </Button>

        <Button
          variant='contained'
          color='primary'
          onClick={handleInternalPreview}
          disabled={isGeneratingPDF}
          startIcon={
            <Box
              sx={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1,
              }}
            >
              <DescriptionOutlinedIcon
                sx={{
                  color: 'white',
                  fontSize: '1rem',
                }}
              />
            </Box>
          }
          sx={{
            px: 2,
            textTransform: 'none',
            fontWeight: 'bold',
            '& .MuiButton-startIcon': {
              marginRight: '8px', // Adjust icon spacing if needed
            },
          }}
        >
          {isGeneratingPDF ? (
            <>
              Generating...
              <CircularProgress size={24} sx={{ ml: 1 }} />
            </>
          ) : (
            'PREVIEW REPORT'
          )}
        </Button>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={8000}
        onClose={() => setPdfError(null)}
        open={Boolean(pdfError)}
      >
        <Alert
          onClose={() => setPdfError(null)}
          severity='error'
          variant='filled'
        >
          {pdfError}
        </Alert>
      </Snackbar>

      {/* Modals */}
      <Dialog
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        {console.log('Editing record:', editingCommentRowId)}
        <DialogTitle>
          {activeTab === 0 ? 'Edit Offer Comment' : 'Edit Feedback Comment'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={editingCommentValue}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue.length <= 255) {
                setEditingCommentValue(newValue);
              }
            }}
            inputProps={{ maxLength: 255 }}
            helperText={
              editingCommentValue.length >= 255
                ? 'Maximum 255 characters reached'
                : `${editingCommentValue.length}/255 characters`
            }
            FormHelperTextProps={{
              sx: {
                color:
                  editingCommentValue.length >= 255
                    ? 'error.main'
                    : 'text.secondary',
              },
            }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentModalOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={async () => {
              try {
                const isOffersTab = activeTab === 0;
                const endpointBase = isOffersTab
                  ? `${API_BASE}/offers`
                  : `${API_BASE}/feedback`;

                const response = await apiFetch(
                  `${endpointBase}/${editingCommentRowId}`,
                  {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      recordId: editingCommentRowId,
                      value: editingCommentValue,
                      field: editingField,
                    }),
                  },
                );

                const result = await response.json();

                if (!response.ok) {
                  console.error('Failed to update comment:', result);
                  alert(`Error: ${result.error || 'Unknown error'}`);
                  return;
                }

                if (isOffersTab) {
                  // ✅ Update offers state
                  setOffers((prev) =>
                    prev.map((offer) =>
                      offer.id === editingCommentRowId
                        ? { ...offer, [editingField]: editingCommentValue }
                        : offer,
                    ),
                  );
                } else {
                  // ✅ Update feedback state based on editingField
                  setFeedback((prev) =>
                    prev.map((fb) =>
                      fb.id === editingCommentRowId
                        ? { ...fb, [editingField]: editingCommentValue }
                        : fb,
                    ),
                  );
                }

                setCommentModalOpen(false);
              } catch (err) {
                console.error('Fetch error:', err);
                alert('Failed to connect to update service.');
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Edit Modal */}
      <Dialog
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Update Feedback Status</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            value={editingStatusValue}
            onChange={(e) => setEditingStatusValue(e.target.value)}
            label='Select Status'
            sx={{ mt: 2 }}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusModalOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={async () => {
              try {
                const endpointBase = `${API_BASE}/feedback`;

                const response = await apiFetch(
                  `${endpointBase}/${editingStatusRowId}`,
                  {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      recordId: editingStatusRowId,
                      status: editingStatusValue,
                    }),
                  },
                );

                const result = await response.json();

                if (!response.ok) {
                  console.error('Failed to update status:', result);
                  alert(`Error: ${result.error || 'Unknown error'}`);
                  return;
                }

                // Update feedback state
                setFeedback((prev) =>
                  prev.map((fb) =>
                    fb.id === editingStatusRowId
                      ? { ...fb, status: editingStatusValue }
                      : fb,
                  ),
                );

                setStatusModalOpen(false);
              } catch (err) {
                console.error('Fetch error:', err);
                alert('Failed to connect to update service.');
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          Edit{' '}
          {editingField === 'per_unit_pricing'
            ? 'Per Unit Pricing'
            : 'Per Lot Pricing'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label='Dollar Amount'
            type='number'
            value={editingPricingValue}
            onChange={(e) => {
              const val = e.target.value;
              // Only allow whole number input
              if (/^\d*$/.test(val)) {
                setEditingPricingValue(val);
              }
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              min: 0,
            }}
            autoFocus
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingModalOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={async () => {
              try {
                const endpointBase = `${API_BASE}/offers`;

                const response = await apiFetch(
                  `${endpointBase}/${editingPricingRowId}`,
                  {
                    method: 'PATCH',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      recordId: editingPricingRowId,
                      field: editingField,
                      value: parseInt(editingPricingValue || '0', 10),
                    }),
                  },
                );

                const result = await response.json();

                if (!response.ok) {
                  console.error('Failed to update pricing:', result);
                  alert(`Error: ${result.error || 'Unknown error'}`);
                  return;
                }

                // Update local state
                setOffers((prev) =>
                  prev.map((offer) =>
                    offer.id === editingPricingRowId
                      ? {
                          ...offer,
                          [editingField]: parseInt(
                            editingPricingValue || '0',
                            10,
                          ),
                        }
                      : offer,
                  ),
                );

                setPricingModalOpen(false);
              } catch (err) {
                console.error('Fetch error:', err);
                alert('Failed to connect to update service.');
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ColumnSettingsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        columns={(activeTab === 0 ? offerColumns : feedbackColumns).map(
          (col) => ({
            ...col,
            label: col.headerName || col.field, // Use headerName if available, fallback to field
          }),
        )}
        allColumns={(activeTab === 0 ? offerColumns : feedbackColumns).map(
          (col) => ({
            ...col,
            label: col.headerName || col.field,
          }),
        )}
        currentSettings={
          activeTab === 0 ? offerColumnSettings : feedbackColumnSettings
        }
        onSave={(updatedSettings) => {
          if (activeTab === 0) {
            setOfferColumnSettings(updatedSettings);
            reportStorage.setItem(
              'listingReportOfferColumns',
              JSON.stringify(updatedSettings),
            );
          } else {
            setFeedbackColumnSettings(updatedSettings);
            reportStorage.setItem(
              'listingReportFeedbackColumns',
              JSON.stringify(updatedSettings),
            );
          }
          setIsModalOpen(false);
        }}
      />
      {console.log(
        'Columns being passed to modal:',
        activeTab === 0 ? offerColumns : feedbackColumns,
      )}

      <RowOrderModal
        open={isRowModalOpen && activeTab === 0} // shared modal toggle
        onClose={() => setIsRowModalOpen(false)}
        rows={activeTab === 0 ? sortedOfferRows : sortedFeedbackRows}
        rowOrder={activeTab === 0 ? offerRowOrder : feedbackRowOrder}
        setRowOrder={activeTab === 0 ? setOfferRowOrder : setFeedbackRowOrder}
        rowType={activeTab === 0 ? 'offers' : 'feedback'}
        sortModel={activeTab === 0 ? offerSortModel : feedbackSortModel}
        onSortModelChange={
          activeTab === 0 ? setOfferSortModel : setFeedbackSortModel
        }
      />
    </Box>
  );
};

ReportBuilder.propTypes = {
  cartItems: PropTypes.array.isRequired,
  setCartItems: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onRemoveItem: PropTypes.func,
  onNavigateToMap: PropTypes.func,
  savedReport: PropTypes.shape({
    title: PropTypes.string,
    column_settings: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    row_order: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    report_data: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }),
  reportGeoData: PropTypes.objectOf(
    PropTypes.shape({
      geometry: PropTypes.object.isRequired,
      properties: PropTypes.shape({
        dealName: PropTypes.string,
        acreage: PropTypes.number,
      }).isRequired,
    }),
  ),
};

export default ReportBuilder;
